import Phaser from 'phaser';
import GameScene from './scenes/GameScene.js';
import { api } from './api.js';
import { connectSocket, on as socketOn, onSocketLifecycle, emit as socketEmit, isConnected as socketIsConnected, disconnectSocket } from './socket.js';
import { getState, setState, clearMyBets, applyServerNow, applyServerRemaining, authoritativeRemainingMs, serverNow } from './store.js';
import { mountHud, showToast, showLockedSweep, showConsolation } from './hud.js';
import { showResultSheet, updateResultSheet, closeAnySheet } from './sheet.js';
import { unlockAudio, playBackground, playOneShot, chime, pauseBackground, resumeBackground, setAudioTheme, withDefaultAudioTheme, getAudioState, playBetTap, stopAllAudio } from './audio.js';
import { haptic, requestBalanceRefresh, closeGame, isInFlutter } from './bridge.js';
import { ROYAL_FERRIS_THEME as THEME } from './scenes/theme.js';

// ---------------- Diagnostics ----------------
//
// `diag.queue(eventObj)` queues a structured event that we ship to the server
// in batches via POST /api/ferris/diag. This is how client-side problems
// (silent socket disconnects, slow boot, locally-blocked taps) end up in
// Railway logs. Cheap and fire-and-forget.
const diag = (() => {
  const queue = [];
  let timer = null;
  function flush() {
    timer = null;
    if (queue.length === 0) return;
    const batch = queue.splice(0, queue.length);
    // Fire-and-forget. Errors are silently swallowed — diag must never block
    // the game loop. The server caps batches to 20 events and truncates
    // string values, so we don't need to clamp here.
    try { api.diag(batch).catch(() => {}); } catch (_) {}
  }
  return {
    queue(ev) {
      if (!ev || typeof ev !== 'object') return;
      queue.push({ ts: Date.now(), ...ev });
      if (queue.length >= 10) {
        if (timer) { clearTimeout(timer); timer = null; }
        flush();
      } else if (!timer) {
        timer = setTimeout(flush, 2000);
      }
    },
    flush,
  };
})();

// Estimate one-way socket staleness from emission timestamp. NOT to be used
// as RTT for clock sync — see store.applyServerNow doc. Only useful as a
// sanity-check that a payload isn't ancient.
function stalenessMs(payload) {
  if (!payload || typeof payload._emittedAt !== 'number') return null;
  return Date.now() - payload._emittedAt;
}

// Detect ?debug=1 query param. Enables a small overlay that surfaces the
// timing primitives so QA can correlate visible HUD with server state.
const DEBUG = typeof window !== 'undefined' && window.location
  && /[?&]debug=1\b/.test(window.location.search);

let __debugOverlay = null;
function mountDebugOverlay() {
  if (!DEBUG || __debugOverlay) return;
  document.body.classList.add('ferris-debug-mode');
  const el = document.createElement('div');
  el.style.cssText = [
    'position:fixed', 'top:8px', 'right:8px', 'z-index:99999',
    'font:11px/1.3 ui-monospace,Menlo,monospace', `color:${THEME.debugText}`,
    `background:${THEME.debugBg}`, 'padding:6px 8px', 'border-radius:6px',
    'pointer-events:none', 'white-space:pre',
  ].join(';');
  document.body.appendChild(el);
  __debugOverlay = el;
  setInterval(() => {
    const s = getState();
    el.textContent = [
      `status      ${s.status}`,
      `serverRem   ${s.serverRemainingMs|0}`,
      `authRem     ${authoritativeRemainingMs()|0}`,
      `hudRem      ${s.remainingMs|0}`,
      `syncAge     ${s.lastSyncReceivedAt ? (Date.now() - s.lastSyncReceivedAt) : -1}`,
      `syncSrc     ${s.lastSyncSource || '-'}`,
      `clockOffset ${(s.clockOffset|0)}`,
      `lastRtt     ${s._clockLastRttMs == null ? '-' : (s._clockLastRttMs|0)}`,
      `bestRtt     ${isFinite(s._clockBestRtt) ? (s._clockBestRtt|0) : '-'}`,
      `samples     ${s._clockSampleCount}`,
      `wsConn      ${socketIsConnected() ? 'yes' : 'NO'}`,
    ].join('\n');
  }, 250);
}

if (typeof window !== 'undefined') {
  // Diagnostic getters always exposed (cheap; no UI). Inspect from devtools.
  window.__FERRIS_DEBUG = {
    getState,
    serverNow: () => serverNow(),
    authoritativeRemainingMs: () => authoritativeRemainingMs(),
    audioState: () => getAudioState(),
    currentScene: () => (window.__FERRIS_GAME && window.__FERRIS_GAME.scene && window.__FERRIS_GAME.scene.getScene
      ? window.__FERRIS_GAME.scene.getScene('GameScene')
      : currentScene()),
    startSpinFromRound: (round, opts) => startSpinAnimationFromRound(round, opts),
    resumeAudio: () => resumeAudioFromGesture(),
    flushDiag: () => diag.flush(),
  };
}

/**
 * Bootstrap:
 *  1. Fetch /api/ferris/config
 *  2. Boot Phaser GameScene
 *  3. Connect socket, fetch current round, hydrate state
 *  4. Drive countdown via local interval, sync remainingMs with server payloads
 *  5. Wire HUD + slot taps -> POST /bet/batch (pod badge updates immediately; balance only after accept)
 *  6. Render result bottom sheet on ferris_round_finished
 */

const CURRENT_USER_ID_KEY = '__ferris_currentUserId';
const BET_BATCH_DELAY_MS = 250;
const BET_BATCH_FINAL_FLUSH_MS = 1200;
const BET_BATCH_MAX_TAPS = 50;
let betQueue = [];
let betFlushTimer = null;
let betFlushInFlight = false;
let betBatchSeq = 0;
let gameDestroyed = false;
let lastAudioResumeAt = 0;
let activeGame = null;
let activeGameScene = null;
let activeSpinKey = null;
let ensureBackgroundAudio = () => {
  unlockAudio();
  resumeBackground();
};

function resumeAudioFromGesture() {
  const now = Date.now();
  if (now - lastAudioResumeAt < 120) return;
  lastAudioResumeAt = now;
  ensureBackgroundAudio();
}

function payloadRemainingMs(payload, fallback = getState().remainingMs) {
  return payload && typeof payload.remainingMs === 'number' && isFinite(payload.remainingMs)
    ? Math.max(0, payload.remainingMs)
    : fallback;
}

function applyAcceptedBalance(balance) {
  const next = Number(balance);
  if (!Number.isFinite(next)) return;
  const current = getState().balance;
  if (current == null || !Number.isFinite(Number(current))) {
    setState({ balance: next });
    return;
  }
  setState({ balance: Math.min(Number(current), next) });
}

function applyAuthoritativeBalance(balance) {
  const next = Number(balance);
  if (!Number.isFinite(next)) return;
  setState({ balance: Math.max(0, Math.floor(next)) });
}

function applyActivity(activity) {
  if (!Array.isArray(activity)) return;
  setState({ activity: activity.slice(0, 20), activityUpdatedAt: Date.now() });
}

function startSpinAnimationFromRound(round, { remainingMs, source = 'unknown', sweep = false, spinSfx = false } = {}) {
  if (!round || round.status !== 'spinning') return false;
  const winningSlotId = Number(round.winningSlotId);
  if (!Number.isFinite(winningSlotId) || winningSlotId <= 0) return false;
  const roundId = round._id || round.roundId;
  if (!roundId) return false;

  const cfg = getState().config || {};
  const spinEndsAtMs = round.spinEndsAt ? new Date(round.spinEndsAt).getTime() : (getState().spinEndsAt || 0);
  const computedRemainingMs = Number.isFinite(Number(remainingMs))
    ? Math.max(0, Number(remainingMs))
    : (spinEndsAtMs ? Math.max(0, spinEndsAtMs - serverNow()) : ((cfg.spinDurationSec || 5) * 1000));
  if (computedRemainingMs <= 0) return false;

  const spinKey = `${roundId}:${winningSlotId}:${spinEndsAtMs || 'open'}`;
  if (activeSpinKey === spinKey) return true;

  const sc = currentScene();
  if (!sc) {
    diag.queue({ event: 'spin_animation_skipped', reason: 'scene_missing', source, roundId, winningSlotId });
    return false;
  }
  if (sc.isSpinning) {
    diag.queue({ event: 'spin_animation_skipped', reason: 'scene_already_spinning', source, roundId, winningSlotId });
    return true;
  }

  activeSpinKey = spinKey;
  if (sweep) showLockedSweep();
  haptic('medium');
  if (spinSfx && cfg.theme && cfg.theme.spinSfxUrl) playOneShot(cfg.theme.spinSfxUrl, 0.7);
  const durationMs = Math.max(2500, computedRemainingMs - 200);
  diag.queue({ event: 'spin_animation_started', source, roundId, winningSlotId, durationMs, remainingMs: computedRemainingMs });

  sc.spinTo(winningSlotId, durationMs).then(() => {
    const s = getState();
    const currentUserId = window[CURRENT_USER_ID_KEY] || null;
    if (s.status !== 'spinning' && s.status !== 'result') return;
    showResultSheet({
      round: { ...round, roundNumber: s.round && s.round.roundNumber },
      winners: null,
      slots: cfg.slots,
      currentUserId,
      myBets: s.myBets,
    });
  });
  return true;
}

async function refreshBalance() {
  try {
    const res = await api.getBalance();
    if (res && typeof res.serverNow === 'number') applyServerNow(res.serverNow, res._rttMs);
    if (res && res.balance != null) applyAuthoritativeBalance(res.balance);
  } catch (_) {}
}

async function refreshActivity() {
  try {
    const res = await api.getActivity(20);
    if (res && typeof res.serverNow === 'number') applyServerNow(res.serverNow, res._rttMs);
    if (res && Array.isArray(res.activity)) applyActivity(res.activity);
  } catch (_) {}
}

function clearBetFlushTimer() {
  if (!betFlushTimer) return;
  clearTimeout(betFlushTimer);
  betFlushTimer = null;
}

function scheduleBetFlush(delayMs = BET_BATCH_DELAY_MS) {
  if (!betQueue.length || betFlushInFlight) return;
  if (betQueue.length >= BET_BATCH_MAX_TAPS || delayMs <= 0) {
    clearBetFlushTimer();
    flushBetQueue().catch(() => {});
    return;
  }
  if (betFlushTimer) return;
  betFlushTimer = setTimeout(() => {
    betFlushTimer = null;
    flushBetQueue().catch(() => {});
  }, delayMs);
}

function enqueueBetTap(tap) {
  betQueue.push(tap);
  const remaining = authoritativeRemainingMs() || getState().remainingMs || 0;
  scheduleBetFlush(remaining > 0 && remaining <= BET_BATCH_FINAL_FLUSH_MS ? 0 : BET_BATCH_DELAY_MS);
}

function rollbackBetTaps(taps) {
  if (!Array.isArray(taps) || taps.length === 0) return;
  const state = getState();
  const currentRoundId = state.round && state.round._id;
  if (!currentRoundId) return;
  const bySlot = {};
  for (const tap of taps) {
    if (tap.roundId && tap.roundId !== currentRoundId) continue;
    const amount = Number(tap.amount || 0);
    if (!Number.isFinite(amount) || amount <= 0) continue;
    const slotKey = String(tap.slotId);
    bySlot[slotKey] = (bySlot[slotKey] || 0) + amount;
  }
  if (Object.keys(bySlot).length === 0) return;
  const nextMyBets = { ...(state.myBets || {}) };
  for (const [slotKey, amount] of Object.entries(bySlot)) {
    const nextAmount = Math.max(0, Number(nextMyBets[slotKey] || 0) - amount);
    if (nextAmount > 0) nextMyBets[slotKey] = nextAmount;
    else delete nextMyBets[slotKey];
  }
  setState({ myBets: nextMyBets });
}

function dropQueuedBetsForRound(roundId) {
  if (!roundId || betQueue.length === 0) return;
  const dropped = [];
  betQueue = betQueue.filter((tap) => {
    if (tap.roundId !== roundId) return true;
    dropped.push(tap);
    return false;
  });
  if (dropped.length > 0) rollbackBetTaps(dropped);
  if (betQueue.length === 0) clearBetFlushTimer();
}

function resetBetQueue() {
  betQueue = [];
  clearBetFlushTimer();
}

async function flushBetQueue() {
  clearBetFlushTimer();
  if (betFlushInFlight || betQueue.length === 0) return;

  const roundId = betQueue[0].roundId;
  const batch = [];
  const remainingQueue = [];
  for (const tap of betQueue) {
    if (tap.roundId === roundId && batch.length < BET_BATCH_MAX_TAPS) batch.push(tap);
    else remainingQueue.push(tap);
  }
  betQueue = remainingQueue;
  if (batch.length === 0) return;

  betFlushInFlight = true;
  const s = getState();
  const clientRttHintMs = isFinite(s._clockBestRtt) ? s._clockBestRtt : (s._clockLastRttMs || null);
  const clientRemainingMs = authoritativeRemainingMs();
  const batchNonce = `${Date.now()}-${betBatchSeq++}-${Math.random().toString(36).slice(2, 8)}`;

  try {
    const res = await api.placeBetBatch({
      roundId,
      taps: batch.map(({ slotId, amount, clientNonce }) => ({ slotId, amount, clientNonce })),
      batchNonce,
      clientRttHintMs,
      clientRemainingMs,
    });
    if (res && res.balance != null) applyAcceptedBalance(res.balance);
    if (res && res.userId) window[CURRENT_USER_ID_KEY] = res.userId;
    const rejectedTaps = Array.isArray(res && res.rejectedTaps)
      ? res.rejectedTaps.map((tap) => ({ ...tap, roundId }))
      : [];
    if (rejectedTaps.length > 0) {
      rollbackBetTaps(rejectedTaps);
      diag.queue({
        event: 'bet_batch_partial_reject',
        roundId,
        tapCount: batch.length,
        rejectedTapCount: rejectedTaps.length,
        firstReason: rejectedTaps[0] && rejectedTaps[0].reason,
      });
    }
    if (DEBUG) {
      try {
        console.info('[ferris]', JSON.stringify({
          event: 'bet_batch_response',
          ok: true,
          tapCount: batch.length,
          acceptedTapCount: (res.acceptedTaps || []).length,
          rejectedTapCount: rejectedTaps.length,
          totalAccepted: res.totalAccepted || 0,
        }));
      } catch (_) {}
    }
  } catch (e) {
    rollbackBetTaps(batch);
    diag.queue({
      event: 'bet_batch_failed',
      roundId,
      tapCount: batch.length,
      error: e && e.message,
      status: (e && e.status) || null,
      serverReqId: e && e.body && e.body.reqId || null,
    });
    if (DEBUG) try { console.info('[ferris]', JSON.stringify({ event: 'bet_batch_response', ok: false, error: e.message, tapCount: batch.length })); } catch (_) {}
    if (e && e.message === 'betting_closed') {
      refreshCurrentRound().catch(() => {});
    } else {
      showToast(prettyError(e.message), 'error');
    }
  } finally {
    betFlushInFlight = false;
    if (betQueue.length > 0) scheduleBetFlush(0);
  }
}

async function main() {
  const __bootStart = Date.now();
  const loadingEl = document.getElementById('loading-overlay');
  const hideLoading = () => loadingEl?.classList.add('hidden');

  // WebView/browser audio often requires a live user gesture. Keep retrying
  // on real interactions because the first gesture can happen before the
  // background track has been created.
  document.addEventListener('pointerdown', resumeAudioFromGesture, { passive: true });
  document.addEventListener('touchstart', resumeAudioFromGesture, { passive: true });
  document.addEventListener('click', resumeAudioFromGesture, { passive: true });

  let config;
  try {
    const cfgRes = await api.getConfig();
    config = {
      ...cfgRes.config,
      theme: withDefaultAudioTheme((cfgRes.config && cfgRes.config.theme) || {}),
    };
    setAudioTheme(config.theme);
    ensureBackgroundAudio = () => {
      unlockAudio();
      playBackground(config.theme && config.theme.musicUrl, 0.35);
    };
    // Pre-select the first quick-bet amount so users can tap a slot immediately
    const firstAmt = (config && config.quickBetAmounts && config.quickBetAmounts[0]) || 0;
    setState({ config, selectedAmount: firstAmt });
    if (cfgRes && typeof cfgRes._rttMs === 'number') {
      // Note: getConfig payload doesn't carry serverNow today; harmless no-op
      // but kept for future-proofing if backend adds it.
    }
  } catch (e) {
    showToast(`Cannot load config: ${e.message}`, 'error', 4000);
    hideLoading();
    return;
  }

  mountDebugOverlay();

  if (!config || !config.enabled) {
    setState({ config });
    showToast('Game is currently disabled by admin.', 'error', 4000);
    hideLoading();
    return;
  }

  // Background music
  setTimeout(() => playBackground(config.theme && config.theme.musicUrl, 0.35), 400);

  // Boot Phaser
  const gameRoot = document.getElementById('game-root');
  const width = window.innerWidth;
  const height = window.innerHeight;
  const game = new Phaser.Game({
    type: Phaser.AUTO,
    parent: gameRoot,
    transparent: true,
    backgroundColor: THEME.transparent,
    scale: {
      mode: Phaser.Scale.RESIZE,
      width,
      height,
    },
    scene: [GameScene],
  });
  activeGame = game;
  if (typeof window !== 'undefined') window.__FERRIS_GAME = game;

  // Wait for scene to be ready
  await new Promise((r) => setTimeout(r, 80));

  const scene = game.scene.getScene('GameScene');
  activeGameScene = scene;
  scene.setOnSlotTapped((slotId) => placeBet(slotId));

  // Mount HUD
  mountHud('hud-root', {
    onClose: () => {
      cleanupFerrisGame();
      closeGame();
    },
  });

  // Wire socket listeners before connecting so the initial ferris_state
  // emitted on subscribe cannot race past the handlers.
  wireSocketEvents();

  // First paint waits for authenticated balance/current round hydration, so
  // the diamond pill is populated when the player enters instead of only after
  // their first accepted bet.
  await Promise.allSettled([
    refreshBalance(),
    refreshActivity(),
    refreshCurrentRound(),
  ]);

  hideLoading();

  // On disconnect/reconnect, force a fresh /round/current pull so the HUD
  // re-syncs as soon as the connection comes back. Without this, the only
  // recovery path is the 5s fallback poll. Also beacon to server so we can
  // see in Railway logs whether silent socket failures are happening.
  onSocketLifecycle('disconnect', (info) => {
    diag.queue({ event: 'ws_disconnect', reason: (info && info.reason) || 'unknown' });
  });
  onSocketLifecycle('reconnect', () => {
    diag.queue({ event: 'ws_reconnect' });
    refreshCurrentRound().catch(() => {});
    refreshBalance().catch(() => {});
    refreshActivity().catch(() => {});
  });
  onSocketLifecycle('connect', () => {
    diag.queue({ event: 'ws_connect' });
    refreshCurrentRound().catch(() => {});
    refreshBalance().catch(() => {});
    refreshActivity().catch(() => {});
  });

  // Connect socket
  await connectSocket();

  // Ping/pong loop for accurate RTT-based clock offset.
  // Every 5s emit ferris_ping{clientSentAt}; on ferris_pong we compute true
  // RTT = Date.now() - clientSentAt (independent of clock skew). First ping
  // fires immediately so our clock locks in within a few hundred ms of boot.
  function sendPing() {
    if (!socketIsConnected()) return;
    socketEmit('ferris_ping', { clientSentAt: Date.now() });
  }
  sendPing();
  const pingTimer = setInterval(sendPing, 5000);
  let periodicSyncTimer = null;
  let activitySyncTimer = null;
  let rafId = 0;
  let cleanedUp = false;
  const beforeUnloadHandler = () => {
    flushBetQueue().catch(() => {});
    cleanupFerrisGame();
  };
  const visibilityHandler = () => {
    if (document.hidden) {
      flushBetQueue().catch(() => {});
      cancelAnimationFrame(rafId);
      pauseBackground();
    } else if (!cleanedUp) {
      rafId = requestAnimationFrame(tickCountdown);
      resumeBackground();
      refreshCurrentRound().catch(() => {});
      refreshBalance().catch(() => {});
      refreshActivity().catch(() => {});
    }
  };
  const cleanupFerrisGame = () => {
    if (cleanedUp) return;
    cleanedUp = true;
    gameDestroyed = true;
    resetBetQueue();
    activeSpinKey = null;
    clearInterval(pingTimer);
    if (periodicSyncTimer) clearInterval(periodicSyncTimer);
    if (activitySyncTimer) clearInterval(activitySyncTimer);
    if (rafId) cancelAnimationFrame(rafId);
    document.removeEventListener('pointerdown', resumeAudioFromGesture);
    document.removeEventListener('touchstart', resumeAudioFromGesture);
    document.removeEventListener('click', resumeAudioFromGesture);
    document.removeEventListener('visibilitychange', visibilityHandler);
    window.removeEventListener('beforeunload', beforeUnloadHandler);
    window.removeEventListener('pagehide', beforeUnloadHandler);
    try { diag.flush(); } catch (_) {}
    try { disconnectSocket(); } catch (_) {}
    try { game.destroy(true); } catch (_) {}
    activeGame = null;
    activeGameScene = null;
    stopAllAudio();
  };
  window.__FERRIS_CLEANUP = cleanupFerrisGame;
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', beforeUnloadHandler);
    window.addEventListener('pagehide', beforeUnloadHandler);
  }

  // Hydrate current round (server also pushes ferris_state on subscribe; this
  // is a belt-and-suspenders backup for first-paint).
  await Promise.allSettled([
    refreshCurrentRound(),
    refreshBalance(),
    refreshActivity(),
  ]);

  // Boot-complete beacon — Railway log will show ferris_client_diag with
  // bootstrapMs so we can correlate slow boots with downstream issues.
  // NOTE: isInFlutter is a const boolean (not a function); calling it as
  // a function was the source of the "Vi is not a function" boot crash.
  diag.queue({ event: 'boot_complete', bootstrapMs: Date.now() - __bootStart, debug: DEBUG, inFlutter: !!isInFlutter });

  // Drive countdown from server-streamed remainingMs via a monotonic delta.
  // This is immune to clockOffset drift: as long as the server keeps emitting
  // remainingMs (every ~1s during betting via ferris_time_sync), the visible
  // countdown stays within ~1s + (Date.now() jitter) of the authoritative
  // server value. The clockOffset path remains as a fallback when the
  // last_sync timestamp itself becomes stale.
  const tickCountdown = () => {
    if (cleanedUp) return;
    const s = getState();
    const authRem = authoritativeRemainingMs();
    const fallbackRem = s.status === 'betting' && s.bettingEndsAt
      ? Math.max(0, s.bettingEndsAt - serverNow())
      : (s.status === 'spinning' && s.spinEndsAt ? Math.max(0, s.spinEndsAt - serverNow()) : 0);
    // Trust the authoritative source while the last sync is fresh (<3s).
    // After that, fall back to clock-offset math so the timer keeps moving
    // even during a transient socket gap.
    const syncAge = s.lastSyncReceivedAt ? Date.now() - s.lastSyncReceivedAt : Infinity;
    const ms = (s.lastSyncReceivedAt && syncAge < 3000) ? authRem : fallbackRem;
    if (ms !== s.remainingMs) setState({ remainingMs: ms });
    if (s.status === 'betting' && betQueue.length > 0 && ms <= BET_BATCH_FINAL_FLUSH_MS) {
      scheduleBetFlush(0);
    }
    rafId = requestAnimationFrame(tickCountdown);
  };
  rafId = requestAnimationFrame(tickCountdown);

  document.addEventListener('visibilitychange', visibilityHandler);

  // Periodic sync (recover from missed events). Reduced from 15s→5s so a
  // dropped socket can’t leave the HUD desynced for more than ~5s.
  periodicSyncTimer = setInterval(() => {
    if (cleanedUp) return;
    refreshCurrentRound().catch(() => {});
  }, 5000);
  activitySyncTimer = setInterval(() => {
    if (cleanedUp) return;
    refreshActivity().catch(() => {});
  }, 15000);
}

async function refreshCurrentRound() {
  try {
    const res = await api.getCurrentRound();
    if (res && typeof res.serverNow === 'number') applyServerNow(res.serverNow, res._rttMs);
    if (res && res.balance != null) applyAuthoritativeBalance(res.balance);
    if (!res || !res.round) {
      setState({ status: 'idle', round: null, remainingMs: 0, bettingEndsAt: 0, spinEndsAt: 0 });
      // Authoritative "no remaining" snapshot too.
      applyServerRemaining(0, 'http');
      return;
    }
    const r = res.round;
    const status = r.status === 'betting' ? 'betting' : (r.status === 'spinning' ? 'spinning' : 'idle');
    const bettingEndsAt = r.bettingEndsAt ? new Date(r.bettingEndsAt).getTime() : 0;
    const spinEndsAt = r.spinEndsAt ? new Date(r.spinEndsAt).getTime() : 0;
    const now = serverNow();
    let remainingMs = res.remainingMs || 0;
    if (status === 'betting' && bettingEndsAt) remainingMs = Math.max(0, bettingEndsAt - now);
    else if (status === 'spinning' && spinEndsAt) remainingMs = Math.max(0, spinEndsAt - now);
    // Use the server-supplied remainingMs as the authoritative source. This is
    // computed by the backend at request-receipt time, so it implicitly accounts
    // for processing delay before the response was sent.
    if (typeof res.remainingMs === 'number') {
      applyServerRemaining(res.remainingMs, 'http');
    }
    setState({
      round: r,
      status,
      remainingMs: Math.max(0, remainingMs),
      bettingEndsAt,
      spinEndsAt,
      betsBySlot: r.betsBySlot || {},
    });
    if (status === 'spinning') {
      startSpinAnimationFromRound(r, {
        remainingMs: Math.max(0, remainingMs),
        source: 'http_current_round',
      });
    }
  } catch (e) {
    // ignore
  }
}

function wireSocketEvents() {
  // Authoritative initial state push (sent by server on subscribe; also a
  // good resync after reconnect). Hydrates everything in one event so the
  // client never needs the HTTP /round/current round-trip in steady state.
  socketOn('ferris_state', (payload) => {
    if (!payload) return;
    if (typeof payload.serverNow === 'number') applyServerNow(payload.serverNow, null);
    if (typeof payload.remainingMs === 'number') applyServerRemaining(payload.remainingMs, 'ws_state');
    if (Array.isArray(payload.activity)) applyActivity(payload.activity);
    const r = payload.round;
    if (!r) {
      setState({ status: 'idle', round: null, remainingMs: 0, bettingEndsAt: 0, spinEndsAt: 0 });
      return;
    }
    const status = r.status === 'betting' ? 'betting' : (r.status === 'spinning' ? 'spinning' : (r.status === 'finished' ? 'result' : 'idle'));
    const bettingEndsAt = r.bettingEndsAt ? new Date(r.bettingEndsAt).getTime() : 0;
    const spinEndsAt = r.spinEndsAt ? new Date(r.spinEndsAt).getTime() : 0;
    setState({
      round: r, status, bettingEndsAt, spinEndsAt,
      remainingMs: payloadRemainingMs(payload),
      betsBySlot: r.betsBySlot || getState().betsBySlot || {},
    });
    if (status === 'spinning') {
      startSpinAnimationFromRound(r, {
        remainingMs: payloadRemainingMs(payload),
        source: 'ws_state',
      });
    }
    if (DEBUG) try { console.info('[ferris]', JSON.stringify({ event: 'state', status, remainingMs: payload.remainingMs })); } catch (_) {}
  });

  // Pong handler: the only place we measure REAL RTT. clientSentAt is what
  // we attached to our ping; subtracting from now gives a true round-trip
  // independent of any clock skew.
  socketOn('ferris_pong', (payload) => {
    if (!payload || typeof payload.clientSentAt !== 'number' || typeof payload.serverNow !== 'number') return;
    const rtt = Date.now() - payload.clientSentAt;
    applyServerNow(payload.serverNow, rtt);
    if (DEBUG) try { console.info('[ferris]', JSON.stringify({ event: 'pong', rtt })); } catch (_) {}
  });

  socketOn('ferris_time_sync', (payload) => {
    if (!payload) return;
    // No reliable RTT for this event (don't poison the offset). We still
    // apply with rtt=null so the EMA gets a low-weight nudge, and we accept
    // the server-supplied remainingMs which is what actually drives the HUD.
    if (typeof payload.serverNow === 'number') applyServerNow(payload.serverNow, null);
    if (typeof payload.remainingMs === 'number') {
      applyServerRemaining(payload.remainingMs, 'ws_time_sync');
      setState({ remainingMs: payloadRemainingMs(payload) });
    }
    const serverStatus = payload.status === 'finished' ? 'result' : payload.status;
    if (serverStatus && getState().status !== serverStatus) {
      refreshCurrentRound().catch(() => {});
    }
    if (DEBUG) try { console.info('[ferris]', JSON.stringify({ event: 'time_sync', remainingMs: payload.remainingMs, status: payload.status, staleness: stalenessMs(payload) })); } catch (_) {}
  });

  socketOn('ferris_round_snapshot', (payload) => {
    if (!payload) return;
    if (typeof payload.serverNow === 'number') applyServerNow(payload.serverNow, null);
    if (typeof payload.remainingMs === 'number') applyServerRemaining(payload.remainingMs, 'ws_snapshot');
    if (Array.isArray(payload.activity)) applyActivity(payload.activity);
    const r = payload.round;
    if (!r) return;
    const status = r.status === 'betting' ? 'betting' : (r.status === 'spinning' ? 'spinning' : (r.status === 'finished' ? 'result' : 'idle'));
    const bettingEndsAt = r.bettingEndsAt ? new Date(r.bettingEndsAt).getTime() : 0;
    const spinEndsAt = r.spinEndsAt ? new Date(r.spinEndsAt).getTime() : 0;
    setState({
      round: r,
      status,
      bettingEndsAt,
      spinEndsAt,
      remainingMs: payloadRemainingMs(payload),
      betsBySlot: r.betsBySlot || getState().betsBySlot,
    });
    if (status === 'spinning') {
      startSpinAnimationFromRound(r, {
        remainingMs: payloadRemainingMs(payload),
        source: 'ws_snapshot',
      });
    }
    if (DEBUG) try { console.info('[ferris]', JSON.stringify({ event: 'snapshot', status, remainingMs: payload.remainingMs })); } catch (_) {}
  });

  socketOn('ferris_round_started', (payload) => {
    const { round } = payload || {};
    if (payload && typeof payload.serverNow === 'number') applyServerNow(payload.serverNow, null);
    if (payload && typeof payload.remainingMs === 'number') applyServerRemaining(payload.remainingMs, 'ws_round_event');
    resetBetQueue();
    clearMyBets();
    // Close any sheet (rules or previous result) so the new betting window is unobstructed.
    closeAnySheet();
    const cfg = getState().config;
    const bettingEndsAt = round && round.bettingEndsAt
      ? new Date(round.bettingEndsAt).getTime()
      : serverNow() + (cfg.roundDurationSec * 1000);
    setState({
      round,
      status: 'betting',
      bettingEndsAt,
      spinEndsAt: 0,
      remainingMs: Math.max(0, bettingEndsAt - serverNow()),
      betsBySlot: {},
      winners: [],
    });
    haptic('light');
    const sc = currentScene();
    if (sc) sc.resetForNewRound();
  });

  socketOn('ferris_activity', (payload) => {
    if (!payload) return;
    if (typeof payload.serverNow === 'number') applyServerNow(payload.serverNow, null);
    if (Array.isArray(payload.activity)) applyActivity(payload.activity);
  });

  socketOn('ferris_bet_placed', (payload) => {
    const { roundId, betsBySlot } = payload || {};
    if (payload && typeof payload.serverNow === 'number') applyServerNow(payload.serverNow, null);
    const cur = getState().round;
    if (cur && cur._id === roundId) {
      setState({ betsBySlot });
    }
  });

  socketOn('ferris_round_spinning', (payload) => {
    const { roundId, winningSlotId, serverSeed, spinEndsAt } = payload || {};
    if (payload && typeof payload.serverNow === 'number') applyServerNow(payload.serverNow, null);
    if (payload && typeof payload.remainingMs === 'number') applyServerRemaining(payload.remainingMs, 'ws_round_event');
    const cur = getState().round;
    if (!cur || cur._id !== roundId) return;
    dropQueuedBetsForRound(roundId);
    const cfg = getState().config;
    const spinEndsAtMs = spinEndsAt ? new Date(spinEndsAt).getTime() : serverNow() + (cfg.spinDurationSec * 1000);
    const remainingMs = Math.max(0, spinEndsAtMs - serverNow());
    const updatedRound = { ...cur, ...(payload && payload.serverSeedHash ? { serverSeedHash: payload.serverSeedHash } : {}), ...(payload && payload.clientSeed ? { clientSeed: payload.clientSeed } : {}), ...(payload && payload.nonce != null ? { nonce: payload.nonce } : {}), winningSlotId, serverSeed };
    setState({
      status: 'spinning',
      remainingMs,
      bettingEndsAt: 0,
      spinEndsAt: spinEndsAtMs,
      round: updatedRound,
    });
    startSpinAnimationFromRound(updatedRound, {
      remainingMs,
      source: 'ws_round_spinning',
      sweep: true,
      spinSfx: true,
    });
  });

  socketOn('ferris_round_finished', (payload) => {
    const { round, winners } = payload || {};
    if (payload && typeof payload.serverNow === 'number') applyServerNow(payload.serverNow, null);
    if (payload && typeof payload.remainingMs === 'number') applyServerRemaining(payload.remainingMs, 'ws_round_event');
    if (payload && Array.isArray(payload.activity)) applyActivity(payload.activity);
    setState({ status: 'result', winners, lastFinishedRound: round });
    const cfg = getState().config;
    const myBets = getState().myBets;
    const currentUserId = window[CURRENT_USER_ID_KEY] || null;
    const myBetTotal = Object.values(myBets).reduce((a, b) => a + b, 0);
    const myWin = (winners || [])
      .filter((w) => w.userId === currentUserId)
      .reduce((sum, w) => sum + (w.payout || 0), 0);
    if (myBetTotal > 0) {
      if (myWin > 0) { chime(true); haptic('success'); if (cfg.theme && cfg.theme.winSfxUrl) playOneShot(cfg.theme.winSfxUrl); }
      else { chime(false); haptic('warning'); if (cfg.theme && cfg.theme.loseSfxUrl) playOneShot(cfg.theme.loseSfxUrl); showConsolation(); }
    } else {
      // Non-betting spectators get a soft acknowledgement too.
      showConsolation();
    }
    // Update session profit (net) and result history
    const prevProfit = getState().sessionProfit || 0;
    const winSlot = (cfg.slots || []).find((s) => s.id === round.winningSlotId);
    const newHistory = [
      {
        slotId: round.winningSlotId,
        emoji: winSlot ? winSlot.emoji : '🎁',
        multiplier: winSlot ? winSlot.multiplier : null,
        band: bandForMultiplier(winSlot ? winSlot.multiplier : 0),
        isNew: true,
      },
      ...(getState().resultHistory || []).map((r) => ({ ...r, isNew: false })),
    ].slice(0, 8);
    setState({
      sessionProfit: prevProfit + (myWin - myBetTotal),
      resultHistory: newHistory,
    });
    // Patch the pre-rendered sheet (or render fresh if it was missed).
    if (!updateResultSheet({ winners, round })) {
      showResultSheet({ round, winners, slots: cfg.slots, currentUserId, myBets });
    }
    requestBalanceRefresh();
    refreshBalance().catch(() => {});
    refreshActivity().catch(() => {});
    setTimeout(() => clearMyBets(), 1000);
  });
}

async function placeBet(slotId) {
  if (gameDestroyed) return;
  ensureBackgroundAudio();
  const state = getState();
  if (!state.config || !state.config.enabled) {
    diag.queue({ event: 'bet_blocked_local', reason: 'feature_disabled', slotId });
    showToast('Game disabled', 'error');
    return;
  }
  // Status gate: if the round isn't in 'betting' state we know the server
  // will reject (status_not_betting). Skipping the round-trip is fine — BUT
  // we MUST beacon every blocked tap so it shows up on Railway as
  // ferris_client_diag. The previous silent version (no beacon) is what
  // caused the "rejected with no log" class of bug; this version is
  // observable end-to-end.
  //
  // We do NOT gate on `bettingEndsAt - serverNow() <= LOCK_MS` because that
  // subtraction depends on the client clock-offset estimate, which is
  // unreliable on cold start. The server's in-flight tap grace handles the
  // boundary race correctly.
  if (state.status !== 'betting') {
    diag.queue({
      event: 'bet_blocked_local',
      reason: 'not_betting',
      slotId,
      clientStatus: state.status,
      authoritativeRemainingMs: authoritativeRemainingMs(),
    });
    showToast(state.status === 'spinning' ? 'Wheel spinning' : 'Wait for next round', 'error');
    return;
  }
  // Server is now the only deadline gate. Client taps are micro-batched to
  // reduce API load, but every tap remains an individual intent inside the
  // batch so the server can accept/reject it precisely.
  const amount = state.selectedAmount;
  if (!amount || amount <= 0) {
    diag.queue({ event: 'bet_blocked_local', reason: 'no_amount', slotId });
    showToast('Pick an amount first', 'error');
    return;
  }
  if (!state.round) {
    diag.queue({ event: 'bet_blocked_local', reason: 'no_round', slotId });
    showToast('No active round', 'error');
    return;
  }

  // Optimistic update
  const slotKey = String(slotId);
  const prev = state.myBets[slotKey] || 0;
  const distinctSlots = Object.keys(state.myBets).filter((k) => state.myBets[k] > 0).length;
  if (prev === 0 && distinctSlots >= (state.config.maxSpotsPerUser || 6)) {
    diag.queue({ event: 'bet_blocked_local', reason: 'too_many_slots', slotId });
    showToast(`Max ${state.config.maxSpotsPerUser} slots`, 'error');
    return;
  }
  const newMyBets = { ...state.myBets, [slotKey]: prev + amount };
  setState({ myBets: newMyBets });
  playBetTap();
  haptic('light');

  const clientNonce = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  enqueueBetTap({ roundId: state.round._id, slotId, amount, clientNonce });
  if (DEBUG) try { console.info('[ferris]', JSON.stringify({ event: 'bet_queued', slotId, amount, queueSize: betQueue.length })); } catch (_) {}
}

function bandForMultiplier(m) {
  if (!m || m <= 0) return 'low';
  if (m < 3) return 'low';
  if (m < 7) return 'mid';
  if (m < 20) return 'high';
  return 'jackpot';
}

function prettyError(code) {
  switch (code) {
    case 'insufficient_diamonds': return 'Not enough diamonds';
    case 'too_many_spots': return 'Too many slots picked';
    case 'betting_closed': return 'Betting closed';
    case 'feature_disabled': return 'Game disabled';
    case 'slot_max_exceeded': return 'Slot bet limit reached';
    case 'amount_below_min': return 'Bet too small';
    case 'invalid_slot': return 'Invalid slot';
    case 'no_active_round': return 'No active round';
    case 'user_id_locked': return 'ID locked accounts can watch only';
    case 'id_locked': return 'ID locked accounts can watch only';
    default: return code || 'Bet failed';
  }
}

function currentScene() {
  if (activeGameScene && activeGameScene.scene && (!activeGameScene.scene.isActive || activeGameScene.scene.isActive())) return activeGameScene;
  if (activeGame && activeGame.scene && activeGame.scene.getScene) {
    const sc = activeGame.scene.getScene('GameScene');
    if (sc) {
      activeGameScene = sc;
      return sc;
    }
  }
  if (typeof window !== 'undefined' && window.__FERRIS_GAME && window.__FERRIS_GAME.scene && window.__FERRIS_GAME.scene.getScene) {
    const sc = window.__FERRIS_GAME.scene.getScene('GameScene');
    if (sc) {
      activeGame = window.__FERRIS_GAME;
      activeGameScene = sc;
      return sc;
    }
  }
  const games = Phaser.GAMES || [];
  for (const g of games) {
    const sc = g.scene && g.scene.getScene && g.scene.getScene('GameScene');
    if (sc) {
      activeGame = g;
      activeGameScene = sc;
      return sc;
    }
  }
  return null;
}

main().catch((e) => {
  console.error('Ferris bootstrap failed', e);
  showToast(`Boot error: ${e.message}`, 'error', 5000);
});
